import React, { useState } from 'react';
import {
  Box,
  Stack,
  TextField,
  Typography,
  Button,
  IconButton,
  InputAdornment,
  Alert,
  useTheme,
} from '@mui/material';
import { Add, Delete, CalendarToday } from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import { format, isValid } from 'date-fns';
import { useItemValidator } from '../../hooks/useItemValidator';
import { fetchWithAuth } from '../../utils/api';

const initialTrade = {
  username: '',
  tradeDate: '',
  itemsSent: '',
  itemsReceived: '',
  notes: '',
  errors: {},
  useDatePicker: false,
};

const isValidDate = (dateStr) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return false;
  const [year, month, day] = dateStr.split('-').map(Number);
  const date = new Date(year, month - 1, day);
  const now = new Date();
  now.setHours(0, 0, 0, 0);
  return (
    date.getFullYear() === year &&
    date.getMonth() === month - 1 &&
    date.getDate() === day &&
    date <= now
  );
};

const AdminAddTrades = () => {
  const theme = useTheme();
  const [trades, setTrades] = useState([{ ...initialTrade }]);
  const [errors, setErrors] = useState([]);
  const [success, setSuccess] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const { validate, loading } = useItemValidator();

  const resetSubmissionState = () => {
    setSuccess(false);
    setErrors([]);
    setSubmitted(false);
  };

  const handleChange = (index, field, value) => {
    const updated = [...trades];
    updated[index][field] = value;

    const errors = { ...updated[index].errors };

    if (field === 'tradeDate') {
      errors.tradeDate = !isValidDate(value) ? 'Invalid date (YYYY-MM-DD)' : '';
    }
    if (['username', 'itemsSent', 'itemsReceived'].includes(field)) {
      errors[field] = !value ? 'This field is required' : '';
    }
    if (field === 'itemsSent' || field === 'itemsReceived') {
      const { errors: parseErrors } = validate(value);
      errors[field] = parseErrors.length ? parseErrors.join('; ') : '';
    }

    updated[index].errors = errors;
    setTrades(updated);
    resetSubmissionState();
  };

  const handleAddTrade = () => {
    setTrades((prev) => [...prev, { ...initialTrade }]);
    resetSubmissionState();
  };

  const handleRemoveTrade = (index) => {
    setTrades((prev) => prev.filter((_, i) => i !== index));
    resetSubmissionState();
  };

  const toggleDatePicker = (index) => {
    const updated = [...trades];
    updated[index].useDatePicker = !updated[index].useDatePicker;
    setTrades(updated);
    resetSubmissionState();
  };

  const handleDatePickerChange = (index, value) => {
    const updated = [...trades];
    const formatted = isValid(value) ? format(value, 'yyyy-MM-dd') : '';
    updated[index].tradeDate = formatted;
    updated[index].errors.tradeDate = !isValidDate(formatted) ? 'Invalid date' : '';
    setTrades(updated);
    resetSubmissionState();
  };

  const isTradeValid = (trade) =>
    trade.username.trim() &&
    isValidDate(trade.tradeDate) &&
    !trade.errors.itemsSent &&
    !trade.errors.itemsReceived &&
    trade.itemsSent.trim() &&
    trade.itemsReceived.trim();

  const isFormValid = trades.length > 0 && trades.every(isTradeValid);

  const handleSubmit = async () => {
    const payload = trades.map(({ errors, useDatePicker, ...fields }) => fields);
    try {
      await fetchWithAuth("/report/bulk", {
        method: "POST",
        body: payload,
      });
      setSuccess(true);
      setErrors([]);
      setSubmitted(true);
    } catch (err) {
      setErrors([err.message || "Failed to submit trades."]);
      setSuccess(false);
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          maxWidth: 800,
          mx: 'auto',
          mt: 5,
          p: 4,
          backgroundColor: theme.palette.background.paper,
          borderRadius: 2,
        }}
      >
        <Typography
          variant="h5"
          sx={{
            fontWeight: 'bold',
            mb: 3,
            color: theme.palette.accent.main,
          }}
        >
          Admin: Add Trades
        </Typography>

        <Stack spacing={4}>
          {errors.length > 0 && (
            <Alert severity="error" sx={{ whiteSpace: 'pre-wrap' }}>
              {errors.join('\n')}
            </Alert>
          )}

          {success && (
            <Alert severity="success">
              Successfully submitted all trades.
            </Alert>
          )}

          {trades.map((trade, index) => (
            <Box
              key={index}
              sx={{
                border: '1px solid',
                borderColor: theme.palette.divider,
                p: 3,
                borderRadius: 2,
              }}
            >
              <Stack spacing={2}>
                <Stack direction="row" justifyContent="space-between" alignItems="center">
                  <Typography variant="subtitle1" sx={{ color: theme.palette.text.primary }}>
                    Trade {index + 1}
                  </Typography>
                  {trades.length > 1 && (
                    <IconButton onClick={() => handleRemoveTrade(index)} color="error">
                      <Delete />
                    </IconButton>
                  )}
                </Stack>

                <TextField
                  label="Username"
                  value={trade.username}
                  onChange={(e) => handleChange(index, 'username', e.target.value)}
                  error={!!trade.errors.username}
                  helperText={trade.errors.username}
                  fullWidth
                />

                {trade.useDatePicker ? (
                  <DatePicker
                    label="Trade Date"
                    value={trade.tradeDate ? new Date(trade.tradeDate + 'T00:00:00') : null}
                    onChange={(value) => handleDatePickerChange(index, value)}
                    maxDate={new Date()}
                    format="yyyy-MM-dd"
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        fullWidth
                        error={!!trade.errors.tradeDate}
                        helperText={trade.errors.tradeDate}
                      />
                    )}
                  />
                ) : (
                  <TextField
                    label="Trade Date (YYYY-MM-DD)"
                    value={trade.tradeDate}
                    onChange={(e) => handleChange(index, 'tradeDate', e.target.value)}
                    error={!!trade.errors.tradeDate}
                    helperText={trade.errors.tradeDate}
                    fullWidth
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton onClick={() => toggleDatePicker(index)}>
                            <CalendarToday sx={{ color: theme.palette.text.primary }} />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                  />
                )}

                <TextField
                  label="Items Sent"
                  value={trade.itemsSent}
                  onChange={(e) => handleChange(index, 'itemsSent', e.target.value)}
                  error={!!trade.errors.itemsSent}
                  helperText={trade.errors.itemsSent}
                  fullWidth
                  multiline
                  inputProps={{ maxLength: 500 }}
                />

                <TextField
                  label="Items Received"
                  value={trade.itemsReceived}
                  onChange={(e) => handleChange(index, 'itemsReceived', e.target.value)}
                  error={!!trade.errors.itemsReceived}
                  helperText={trade.errors.itemsReceived}
                  fullWidth
                  multiline
                  inputProps={{ maxLength: 500 }}
                />

                <TextField
                  label="Notes (optional)"
                  value={trade.notes}
                  onChange={(e) => handleChange(index, 'notes', e.target.value)}
                  fullWidth
                  multiline
                  inputProps={{ maxLength: 500 }}
                />
              </Stack>
            </Box>
          ))}

          <Button
            startIcon={<Add />}
            onClick={handleAddTrade}
            variant="outlined"
            sx={{
              color: theme.palette.text.primary,
              borderColor: theme.palette.text.primary,
              alignSelf: 'start',
            }}
          >
            Add Another Trade
          </Button>

          <Button
            variant="contained"
            color="primary"
            disabled={!isFormValid || loading || submitted}
            onClick={handleSubmit}
          >
            Submit Trades
          </Button>
        </Stack>
      </Box>
    </LocalizationProvider>
  );
};

export default AdminAddTrades;